import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { ChevronLeft, MapPin, Building2, Calendar, FileText, ExternalLink } from 'lucide-react';

// Assets
import Makkah from "../assets/Projects/Makkah/Makkah.png";
import Medina from "../assets/Projects/Makkah/Medina.png";
import Neom1 from "../assets/Projects/Neom/Neom1.png";
import Neom2 from "../assets/Projects/Neom/Neom2.png";
import Neom3 from "../assets/Projects/Neom/Neom3.png";
import Neom4 from "../assets/Projects/Neom/Neom4.png";
import Doha from "../assets/Projects/doha/Doha.png";
import Doha2 from "../assets/Projects/doha/Doha2.png";
import Doha3 from "../assets/Projects/doha/Doha3.png";
import Doha4 from "../assets/Projects/doha/Doha4.png";
import Doha5 from "../assets/Projects/doha/Doha5.png";
import Doha6 from "../assets/Projects/doha/Doha6.png";
import KSA from "../assets/Projects/KSA_Project/KSA.png";
import KSA2 from "../assets/Projects/KSA_Project/KSA2.png";
import KSA3 from "../assets/Projects/KSA_Project/KSA3.png";
import KSA4 from "../assets/Projects/KSA_Project/KSA4.png";
import KSA5 from "../assets/Projects/KSA_Project/KSA5.png";
import KSA6 from "../assets/Projects/KSA_Project/KSA6.png";
import USA from "../assets/Projects/UsaProject/USA.png";
import USA2 from "../assets/Projects/UsaProject/USA2.png";
import USA3 from "../assets/Projects/UsaProject/USA3.png";
import USA4 from "../assets/Projects/UsaProject/USA4.png";
import USA5 from "../assets/Projects/UsaProject/USA5.png";
import USA6 from "../assets/Projects/UsaProject/USA6.png";
import USA7 from "../assets/Projects/UsaProject/USA7.png";
import USA8 from "../assets/Projects/UsaProject/USA8.png";
import USA9 from "../assets/Projects/UsaProject/USA9.png";
import USA10 from "../assets/Projects/UsaProject/USA10.png";
import Kuwait2 from "../assets/Projects/Kuwait/Kuwait2.png";
import Kuwait from "../assets/Projects/Kuwait/Kuwait.png";
import USA3Project from "../assets/Projects/UsaProject3/USA-Project-3.png";
import USA4Project from "../assets/Projects/UsaProject3/USA-Project-4.png";
import USA1Project from "../assets/Projects/UsaProject2/USA-Project-1.png";
import USA2Project from "../assets/Projects/UsaProject2/USA-Project-2.png";

const projects = [
  {
    id: 1,
    title: 'Makkah & Medina Saudi Arabia',
    category: 'Sacred Architecture',
    description: 'Third Saudi Expansion of Holy Haram, Makkah (Project Nr: 2020/5454).',
    image: Makkah,
    details: 'The Third Saudi Expansion Prophet’s Mosque, Piazzas and its Related Elements Project- Medina.',
    gallery: [Makkah, Medina],
  },
  {
    id: 2,
    title: 'Neom Projects Saudi Arabia',
    category: 'Giga-Project',
    description: 'Sooners Land has Prepared Bidding Package for Contractors. Neom, KSA',
    image: Neom2,
    details: 'Strategic bidding and planning for the world\'s most ambitious smart city project.',
    gallery: [Neom1, Neom2, Neom3, Neom4],
  },
  {
    id: 3,
    title: 'Zekreet, Qatar',
    category: 'Naval Defense',
    description: 'Doha Naval Base Project',
    image: Doha,
    details: 'The Doha Naval Base Project is located in Zekreet, Qatar. The project includes the construction of a high-security naval base and state-of-the-art related facilities.',
    gallery: [Doha2, Doha3, Doha4, Doha5, Doha6],
  },
  {
    id: 4,
    title: 'Jizzan to Empty Quarter Saudi Arabia',
    category: 'Highway Infrastructure',
    description: 'South Border Infrastructure (1600 Km road Construction and Supervision)',
    image: KSA,
    details: 'Large scale road construction and supervision crossing challenging desert terrain.',
    gallery: [KSA2, KSA3, KSA4, KSA5, KSA6],
  },
  {
    id: 5,
    title: 'Red River Bridge USA',
    category: 'Federal Infrastructure',
    description: 'Project Management connecting State of Oklahoma with State of Texas.',
    image: USA,
    details: 'Infrastructure Federal Highway Projects management and coordination.',
    gallery: [USA2, USA3, USA4, USA5, USA6, USA7, USA8, USA9, USA10],
  },
  {
    id: 6,
    title: 'Houston, Texas, USA',
    category: 'Aviation & Refinery',
    description: 'George Bush International Airport Project (Materials Testing) in Houston.',
    image: USA3Project,
    details: 'Tenneco Oil Refinery Project (Pile Foundations Inspection) in Houston, Texas, USA.',
    gallery: [USA3Project, USA4Project],
  },
  {
    id: 7,
    title: 'State of Tennessee, USA',
    category: 'Environmental Engineering',
    description: 'Surface Mining Reclamation Projects',
    image: USA1Project,
    details: 'Specialized environmental restoration and mining area reclamation.',
    gallery: [USA1Project, USA2Project],
  },
  {
    id: 8,
    title: 'Al-Jahra, Kuwait',
    category: 'Urban Housing',
    description: 'Housing Projects Construction, Kuwait',
    image: Kuwait,
    details: 'Comprehensive construction and project management for large scale housing developments.',
    gallery: [Kuwait2],
  },
];

const ProjectDetails = () => {
  const { id } = useParams();
  const project = projects.find((p) => p.id === parseInt(id));

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
           <h1 className="text-4xl font-bold mb-4">Project Not Found</h1>
           <Link to="/projects" className="btn-primary">Back to Portfolio</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-50 min-h-screen">
      <Helmet>
        <title>{project.title} | Projects | Sooners Land LLC</title>
        <meta name="description" content={project.description} />
      </Helmet>

      {/* Hero Header */}
      <section className="relative h-[60vh] flex items-end pb-20 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center z-0"
          style={{ backgroundImage: `url(${project.image})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-primary via-primary/60 to-transparent z-10" />
        </div>

        <div className="container mx-auto px-6 md:px-12 relative z-20">
           <Link 
            to="/projects" 
            className="inline-flex items-center gap-2 text-white/70 hover:text-white mb-8 transition-colors text-sm font-bold uppercase tracking-widest"
           >
             <ChevronLeft size={18} /> Back to Projects
           </Link>
           <motion.div
             initial={{ opacity: 0, y: 30 }}
             animate={{ opacity: 1, y: 0 }}
           >
              <span className="px-4 py-1.5 bg-accent text-white rounded-full text-xs font-bold uppercase tracking-widest mb-6 inline-block">
                {project.category}
              </span>
              <h1 className="text-4xl md:text-6xl font-bold text-white max-w-4xl leading-tight">
                {project.title}
              </h1>
           </motion.div>
        </div>
      </section>

      <div className="container mx-auto px-6 md:px-12 py-16">
        <div className="grid lg:grid-cols-3 gap-12">
          
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-12">
            <section className="glass-card bg-white p-10 md:p-14 rounded-[3rem] shadow-xl border border-slate-100">
               <h2 className="text-3xl font-bold mb-8 text-primary flex items-center gap-3">
                  <div className="w-10 h-10 bg-accent-soft rounded-lg flex items-center justify-center">
                    <FileText size={20} className="text-accent" />
                  </div>
                  Project Overview
               </h2>
               <p className="text-xl text-text-muted leading-relaxed mb-8">
                  {project.description}
               </p>
               <p className="text-lg text-text-muted leading-relaxed">
                  {project.details}
               </p>
            </section>

            <section>
              <h3 className="text-2xl font-bold mb-8 text-primary px-4 border-l-4 border-accent">Project Gallery</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {project.gallery.map((image, index) => (
                  <motion.div 
                    key={index}
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    className="rounded-3xl overflow-hidden shadow-lg border border-slate-200 group"
                  >
                    <img
                      src={image}
                      alt={`${project.title} view ${index + 1}`}
                      className="w-full h-80 object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </motion.div>
                ))}
              </div>
            </section>
          </div>

          {/* Sidebar */}
          <div className="space-y-8">
             <div className="bg-primary text-white p-10 rounded-[2.5rem] shadow-2xl">
                <h3 className="text-xl font-bold mb-8 flex items-center gap-2 border-b border-white/10 pb-4">
                  Project Details
                </h3>
                <div className="space-y-6">
                   <div className="flex gap-4">
                      <MapPin className="text-accent shrink-0" size={20} />
                      <div>
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">Location</p>
                        <p className="font-semibold">{project.title.split(',').pop()?.trim()}</p>
                      </div>
                   </div>
                   <div className="flex gap-4">
                      <Building2 className="text-accent shrink-0" size={20} />
                      <div>
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">Sector</p>
                        <p className="font-semibold">Infrastructure & Engineering</p>
                      </div>
                   </div>
                   <div className="flex gap-4">
                      <Calendar className="text-accent shrink-0" size={20} />
                      <div>
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">Status</p>
                        <p className="font-semibold">Successfully Delivered</p>
                      </div>
                   </div>
                </div>
                
                <div className="mt-12">
                   <Link to="/contact" className="btn-primary w-full shadow-lg shadow-accent/20">
                      Inquire About Project
                   </Link>
                </div>
             </div>

             <div className="glass-card bg-white p-8 rounded-3xl border border-slate-200 text-center">
                <h4 className="font-bold mb-4">View Full Profile</h4>
                <p className="text-sm text-text-muted mb-6">Need more technical details? Download our comprehensive company profile.</p>
                <Link to="/company-profile" className="text-accent font-bold flex items-center justify-center gap-2 hover:underline">
                   Technical Documentation <ExternalLink size={16} />
                </Link>
             </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default ProjectDetails;